import {getLanguageName} from "@/utils/textFormatting";
import {AudioTrack, MediaMetaData, SubtitleCodec, SubtitleTrack, VideoTrack} from "@/types";
import FFmpegClient from "./FFmpegClient";
import {OPFSQuotaExceededError} from "./OPFSService";

export class FFmpegError extends Error {
    constructor(
        public readonly exitCode: number,
        public readonly log: string
    ) {
        const lastLines = log.trim().split('\n').slice(-20).join('\n');
        super(`FFmpeg failed with exit code ${exitCode}:\n${lastLines}`);
        this.name = 'FFmpegError';
    }
}

type DrmStatusResult = {
    drmStatus: Map<number, boolean>,
    globalDrm: boolean
}

const MAX_LOG_CHARS = 512 * 1024;
const LOG_COMPACT_THRESHOLD = 4096;

/**
 * FFmpeg can emit millions of log lines on a long job, which overflows the
 * engine's maximum string length if naively concatenated. Only the tail is
 * ever needed, so older lines are dropped as new ones arrive.
 */
class BoundedLog {
    private lines: string[] = [];
    private start = 0;
    private length = 0;

    public append(message: string): void {
        const line = message + '\n';
        this.lines.push(line);
        this.length += line.length;

        while (this.length > MAX_LOG_CHARS && this.start < this.lines.length - 1) {
            this.length -= this.lines[this.start++].length;
        }

        if (this.start >= LOG_COMPACT_THRESHOLD) {
            this.lines = this.lines.slice(this.start);
            this.start = 0;
        }
    }

    public toString(): string {
        return this.lines.slice(this.start).join('');
    }
}

export default class FFmpegService extends FFmpegClient {

    protected async executeCommand(args: string[]): Promise<string> {
        const log = new BoundedLog();

        const logger = ({ message }: { message: string }) => {
            log.append(message);
        };

        this.on('log', logger);

        try {
            await this.exec(args);
        } finally {
            this.off('log', logger);
        }

        return log.toString();
    }

    protected getMetadata(inputFile: string): Promise<string> {
        return this.executeCommand(["-i", inputFile]);
    }

    private parseDuration(durationStr: string): number {
        const parts = durationStr.split(':');
        if (parts.length === 3) {
            return (parseInt(parts[0]) * 3600) +
                (parseInt(parts[1]) * 60) +
                parseFloat(parts[2]);
        }
        return 0;
    }

    private getStreamDurations(metadata: string): Map<number, number> {
        const durations = new Map<number, number>();

        let match;
        const pattern = /Stream #0:(\d+).*?Duration: ([0-9:.]+)/g;
        while ((match = pattern.exec(metadata)) !== null) {
            durations.set(parseInt(match[1]), this.parseDuration(match[2]));
        }

        return durations;
    }

    private getStreamDrmStatus(metadata: string): DrmStatusResult {
        const drmStatus = new Map<number, boolean>();
        let match;

        const pattern1 = /Stream #0:(\d+).*?(encrypted|protected|drm|encryption)/gi;
        while ((match = pattern1.exec(metadata)) !== null) {
            drmStatus.set(parseInt(match[1]), true);
        }

        const pattern2 = /Stream #0:(\d+).*?codec_name=none.*?format is drm/gi;
        while ((match = pattern2.exec(metadata)) !== null) {
            drmStatus.set(parseInt(match[1]), true);
        }

        const globalDrm = /(drmi|drms|encrypted container)/i.test(metadata);

        return { drmStatus, globalDrm };
    }

    public async getMediaMetaData(inputFile: string): Promise<MediaMetaData> {
        const metadata = await this.getMetadata(inputFile);

        const subtitles: SubtitleTrack[] = [];
        const videos: VideoTrack[] = [];
        const audio: AudioTrack[] = [];

        const streamDurations = this.getStreamDurations(metadata);
        const { drmStatus, globalDrm } = this.getStreamDrmStatus(metadata);

        let globalDuration = 0;
        const durationMatch = /Duration:\s*([0-9]{2}):([0-9]{2}):([0-9]{2}.[0-9]{0,2})/m.exec(metadata);
        if (durationMatch) {
            globalDuration = (parseInt(durationMatch[1]) * 3600) +
                (parseInt(durationMatch[2]) * 60) +
                parseFloat(durationMatch[3]);
        }

        const streamBlocks = metadata.match(/Stream #0:\d+(?:[\s\S]*?)(?=Stream #|$)/g) || [];

        for (const block of streamBlocks) {
            const streamMatch = /Stream #0:(\d+)/.exec(block);
            if (!streamMatch) continue;

            const streamNumber = parseInt(streamMatch[1]);
            const languageMatch = /\((\w+)\)/.exec(block);
            const language = languageMatch ? getLanguageName(languageMatch[1]) : undefined;
            const duration = streamDurations.get(streamNumber) || (globalDuration > 0 ? globalDuration : 0);

            if (block.includes(': Video:')) {
                const codecMatch = /Video: (\w+)/.exec(block);
                const codec = codecMatch ? codecMatch[1] : '';

                const dimensionsMatch = /Video: .+, (\d+)x(\d+)/.exec(block);
                const width = dimensionsMatch ? parseInt(dimensionsMatch[1]) : 0;
                const height = dimensionsMatch ? parseInt(dimensionsMatch[2]) : 0;

                const fpsMatch = /(\d+(?:\.\d+)?)\s*fps/.exec(block);
                const fps = fpsMatch ? parseFloat(fpsMatch[1]) : 30;

                videos.push({
                    streamNumber,
                    language,
                    codec,
                    duration,
                    width,
                    height,
                    fps,
                    drmProtected: drmStatus.get(streamNumber) || globalDrm || false
                });
            } else if (block.includes(': Audio:')) {
                const codecMatch = /Audio: (\w+)/.exec(block);
                const codec = codecMatch ? codecMatch[1] : '';

                audio.push({
                    streamNumber,
                    duration,
                    codec,
                    language,
                    drmProtected: drmStatus.get(streamNumber) || globalDrm || false
                });
            } else if (block.includes(': Subtitle:')) {
                const codecMatch = /Subtitle: (\w+)/.exec(block);
                const codec = codecMatch ? codecMatch[1] as SubtitleCodec : 'subrip';

                subtitles.push({
                    streamNumber,
                    language: language || 'Unknown',
                    codec,
                    duration
                });
            }
        }

        return { subtitles, videos, audio };
    }

    public async embedSubtitle(
        inputVideoPath: string,
        subtitleData: ArrayBuffer,
        language: string,
        subtitleType: 'srt' | 'ass',
        opfsOutputPath: string,
        onProgress: (progress: number) => void,
        signal?: AbortSignal,
    ): Promise<void> {
        const subtitlePath = `/tmp/subtitle.${subtitleType}`;
        const virtualOutputPath = `/dev/output.mkv`;
        const subtitleCodec = 'copy';

        const ffmpegLog = new BoundedLog();
        const logHandler = ({message}: { message: string }) => {
            ffmpegLog.append(message);
        };

        const progressHandler = (event: {progress: number}) => {
            onProgress(event.progress)
        }

        this.on('log', logHandler);
        this.on('progress', progressHandler)

        try {
            await this.writeFile(subtitlePath, new Uint8Array(subtitleData));
            await this.mountOpfs(opfsOutputPath, virtualOutputPath);

            const mediaInfo = await this.getMediaMetaData(inputVideoPath);
            const existingSubtitleCount = mediaInfo.subtitles.length;

            const ffmpegArgs = [
                '-i', inputVideoPath,
                '-i', subtitlePath,
                '-c:v', 'copy',
                '-c:a', 'copy',
                '-c:s', subtitleCodec,
                '-map', '0:V?',
                '-map', '0:a?',
                '-map', '0:s?',
                '-map', '1:0',
                '-metadata:s:s:' + existingSubtitleCount, 'language=' + language,
                virtualOutputPath
            ];

            const exitCode = await this.exec(ffmpegArgs, -1, {signal});

            if (exitCode !== 0) {
                const hasQuotaError = await this.checkOpfsQuotaError();
                if (hasQuotaError) {
                    throw new OPFSQuotaExceededError();
                }
                throw new FFmpegError(exitCode, ffmpegLog.toString());
            }
        }  catch (err: any) {
            if (err.name === 'AbortError') {
                this.terminate();
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            throw err;
        } finally {
            this.off('log', logHandler);
            this.off('progress', progressHandler);
            await this.closeOpfs(virtualOutputPath);
            await this.deleteFile(subtitlePath);
        }
    }
}
